import { Button } from "@/components/ui/baseUi/button"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import SearchEmpty from "@images/empty/search_empty.svg"
import ExploreTraining from "@images/exploreTraining.png"
import { BenchmarkInfoResume } from "@modules/benchmark/components/benchmarkInfoResume"
import { BoxInfoResume } from "@modules/box/components/boxInfoResume"
import { word } from "@modules/core/utils/i18n"
import { ExploreCard } from "@modules/explore/components/exploreCard/exploreCard"
import { ExploreContentEntity } from "@modules/explore/entities/exploreEntity"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { useExploreSearchContents } from "@modules/explore/hooks/useExplore"
import { PublicProfileInfoResume } from "@modules/profile/publicProfile/components/publicProfileInfoResume"
import { ProgramBundleInfoWrapper } from "@modules/programBundle/components/programBundleInfoWrapper"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EmptyView } from "@modules/ui/components/emptyView"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { Search, X } from "lucide-react"
import { Suspense, useCallback, useEffect, useRef, useState } from "react"
import { Text } from "@/components/ui/baseUi/text"
import { ErrorBoundary } from "react-error-boundary"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import useDebounce from "@/modules/hooks/useDebounce"

export function ExplorePage({ ...props }: React.ComponentProps<"div">) {
  const [searchTerms, setSearchTerms] = useState("")
  const [selectedItem, setSelectedItem] = useState("")
  const [filter, setFilter] = useState<ExploreFilter>(ExploreFilter.DEFAULT)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const debouncedSearchTerms = useDebounce(searchTerms, 300)
  const {
    data: { content: exploreContents },
    isLoading: loadingExploreContents,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useExploreSearchContents(filter, debouncedSearchTerms)
  const { sidebarNavigateTo } = useSidebar()
  const { navigateToProgramView } = useProgramSidebar()

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection)

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [handleIntersection])

  const onViewDetailsClick = (itemId: string, itemTypeOf: ExploreFilter) => {
    switch (itemTypeOf) {
      case ExploreFilter.PROGRAM:
      case ExploreFilter.PROGRAM_PRO:
        navigateToProgramView(itemId, false, {
          onHide: () => {
            setSelectedItem("")
            return Promise.resolve(true)
          },
        })

        break
      case ExploreFilter.PROGRAM_BUNDLE:
        void sidebarNavigateTo({
          content: (
            <ErrorBoundary fallback={<ErrorView />}>
              <Suspense fallback={<LoadingSpinner />}>
                <ProgramBundleInfoWrapper programBundleId={itemId} />
              </Suspense>
            </ErrorBoundary>
          ),
          replaceAllComponents: true,
          onHide: () => {
            setSelectedItem("")
            return Promise.resolve(true)
          },
        })
        break

      case ExploreFilter.BOX:
        void sidebarNavigateTo({
          content: (
            <ErrorBoundary fallback={<ErrorView />}>
              <Suspense fallback={<LoadingSpinner />}>
                <BoxInfoResume boxId={itemId} />
              </Suspense>
            </ErrorBoundary>
          ),
          replaceAllComponents: true,
          onHide: () => {
            setSelectedItem("")
            return Promise.resolve(true)
          },
        })
        break
      case ExploreFilter.BENCHMARK:
        void sidebarNavigateTo({
          content: (
            <ErrorBoundary fallback={<ErrorView />}>
              <Suspense fallback={<LoadingSpinner />}>
                <BenchmarkInfoResume benchmarkId={itemId} />
              </Suspense>
            </ErrorBoundary>
          ),
          replaceAllComponents: true,
          onHide: () => {
            setSelectedItem("")
            return Promise.resolve(true)
          },
        })
        break
      case ExploreFilter.PROFILE:
        void sidebarNavigateTo({
          content: (
            <ErrorBoundary fallback={<ErrorView />}>
              <Suspense fallback={<LoadingSpinner />}>
                <PublicProfileInfoResume userId={itemId} />
              </Suspense>
            </ErrorBoundary>
          ),
          replaceAllComponents: true,
          onHide: () => {
            setSelectedItem("")
            return Promise.resolve(true)
          },
        })
        break
    }
    setSelectedItem(itemId) // onHide call setSelectedItem in the switch, so it's necessary to call setSelectedItem here
  }

  const returnContent = useCallback(
    (content: ExploreContentEntity, index: number) => {
      let itemContent
      if (content.program) {
        itemContent = content.program
      } else if (content.box) {
        itemContent = content.box
      } else if (content.benchmark) {
        itemContent = content.benchmark
      } else if (content.profile) {
        itemContent = content.profile
      } else {
        throw new Error("No Explore Content find")
      }
      return (
        <ExploreCard
          item={itemContent}
          key={index}
          onViewDetailsClick={onViewDetailsClick}
          isSelected={selectedItem === itemContent.id}
        />
      )
    },
    [selectedItem, onViewDetailsClick],
  )

  return (
    <div className="relative flex h-auto w-full flex-col p-6" {...props}>
      {/* HEADER */}
      <div className="flex flex-col items-center gap-8 py-8">
        <div className="flex items-center justify-center">
          <img src={ExploreTraining} className="h-auto max-w-full" />
        </div>
        <div className="flex flex-col items-center gap-4">
          <InputGroup className="w-[70%] xl:w-3xl">
            <InputGroupInput
              placeholder={word("searchbar.placeholder")}
              onChange={(e) => setSearchTerms(e.currentTarget.value)}
              value={searchTerms}
            />
            <InputGroupAddon align="inline-start">
              <Search />
            </InputGroupAddon>
            {searchTerms && (
              <InputGroupAddon align="inline-end">
                <X className="size-4 cursor-pointer" onClick={() => setSearchTerms("")} />
              </InputGroupAddon>
            )}
          </InputGroup>

          <div className="mt-2 flex flex-wrap justify-center gap-2">
            <Button
              variant={filter === ExploreFilter.DEFAULT ? "primary" : "outline"}
              onClick={() => setFilter(ExploreFilter.DEFAULT)}
            >
              <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
                {word("explore.search.filter.discover")}
              </Text>
            </Button>
            <Button
              variant={filter === ExploreFilter.PROFILE ? "primary" : "outline"}
              onClick={() => setFilter(ExploreFilter.PROFILE)}
            >
              <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
                {word("explore.search.filter.user")}
              </Text>
            </Button>
            <Button
              variant={filter === ExploreFilter.PROGRAM ? "primary" : "outline"}
              onClick={() => setFilter(ExploreFilter.PROGRAM)}
            >
              <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
                {word("menu.program.detail")}
              </Text>
            </Button>
            <Button
              variant={filter === ExploreFilter.BENCHMARK ? "primary" : "outline"}
              onClick={() => setFilter(ExploreFilter.BENCHMARK)}
            >
              <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
                {word("global.benchmark.name")}
              </Text>
            </Button>
            <Button
              variant={filter === ExploreFilter.BOX ? "primary" : "outline"}
              onClick={() => setFilter(ExploreFilter.BOX)}
            >
              <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
                {word("global.box.name")}
              </Text>
            </Button>
          </div>
        </div>
      </div>

      {loadingExploreContents ? (
        <LoadingSpinner />
      ) : (
        <>
          {exploreContents.length > 0 ? (
            <>
              <div className="mx-auto flex h-auto w-full flex-wrap justify-center gap-4">
                {exploreContents.map((content, i) => returnContent(content, i))}
              </div>

              {(hasNextPage || isFetchingNextPage) && (
                <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center">
                  <Text fontStyle="BM">{word("global.loading")}</Text>
                </div>
              )}
            </>
          ) : (
            <EmptyView
              image={{ src: SearchEmpty }}
              title={word("explore.search.no_result.title", { searchTerms })}
              description={word("explore.search.no_result.message")}
              flexGrow={false}
              className="flex justify-center bg-surface-background p-6"
              textAlign="left"
              disposition="row-reverse"
            />
          )}
        </>
      )}
    </div>
  )
}
